import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';

const POLICY = {
  none: ['Monitoring only', 'warn', 'Email that fails is still delivered. Fine while you check every service you use is set up; then move on.'],
  quarantine: ['Quarantine', 'neutral', 'Email that fails goes to spam. One step from the strongest setting.'],
  reject: ['Reject', 'good', 'Email that fails is refused: nobody can send as this domain. The strongest setting.'],
};
const KIND = {
  yours: ['Yours', 'good'], authorised: ['Passes', 'good'], mixed: ['Some failing', 'warn'], spoofing: ['Failing', 'bad'],
};

// Monitoring → quarantine → reject, one step at a time
function PolicySteps({ policy }) {
  const steps = [['none', 'Get reports (p=none)'], ['quarantine', 'Failing email to spam (p=quarantine)'], ['reject', 'Refuse failing email (p=reject)']];
  const at = steps.findIndex(([k]) => k === policy);
  return (
    <ol className="dmarc-steps">
      {steps.map(([k, label], i) => <li key={k} className={i < at ? 'is-done' : i === at ? 'is-current' : ''}>{label}</li>)}
    </ol>
  );
}

function DayChart({ days }) {
  const max = Math.max(1, ...days.map((d) => d.passed + d.failed));
  return (
    <div className="dmarc-chart" role="img" aria-label="Email sent as your domain each day: passing and failing">
      {days.map((d) => (
        <div key={d.day} className="dmarc-bar" title={`${d.day}: ${d.passed} passed, ${d.failed} failed`}>
          <span className="dmarc-bar-fail" style={{ height: `${(d.failed / max) * 100}%` }} />
          <span className="dmarc-bar-pass" style={{ height: `${(d.passed / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
}

export default function EmailSecurity() {
  const { organisation } = useAuth();
  const base = organisation ? `/portal/organisations/${organisation.id}/email-security` : '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!base) return;
    try { setData(await api(base)); } catch (err) { setError(err.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const createAddress = async () => {
    setBusy(true);
    try { await api(`${base}/address`, { method: 'POST' }); await load(); } catch (err) { setError(err.message); }
    setBusy(false);
  };
  const mark = async (ip, status) => {
    try { await api(`${base}/sources/${encodeURIComponent(ip)}`, { method: 'POST', body: { status } }); await load(); } catch (err) { setError(err.message); }
  };

  if (!data) return <div className="page"><PageHeader title="Email security" />{error ? <Alert tone="danger">{error}</Alert> : <Loading />}</div>;
  const totals = data.days.reduce((t, d) => ({ passed: t.passed + d.passed, failed: t.failed + d.failed }), { passed: 0, failed: 0 });

  return (
    <div className="page">
      <PageHeader title="Email security" subtitle="Mail providers report every day who sent email claiming to be your domain. We read the reports, so you can see anyone pretending to be you — and safely tell providers to refuse them." />
      {error && <Alert tone="danger">{error}</Alert>}

      <Section title="Set up reports" description="One change to your DNS: add our address to your DMARC record. Your other report addresses keep working.">
        {!data.address ? (
          data.can_manage
            ? <button type="button" className="btn btn-primary" disabled={busy} onClick={createAddress}>{busy ? 'Creating…' : 'Get your report address'}</button>
            : <p className="muted">An owner or admin of your company can set this up.</p>
        ) : (
          <>
            <p>Your report address: <code className="mono">{data.address}</code> <CopyButton text={data.address} /></p>
            {!data.domains.length && <p className="muted">Verify a domain first (Company &amp; domains), then come back here.</p>}
            <ul className="dmarc-domains">
              {data.domains.map((d) => (
                <li key={d.domain} className="card-inset">
                  <div className="dmarc-domain-head">
                    <strong className="mono">{d.domain}</strong>
                    {d.policy ? <Pill tone={POLICY[d.policy]?.[1] || 'neutral'}>{POLICY[d.policy]?.[0] || d.policy}</Pill> : <Pill tone="bad">No DMARC record</Pill>}
                    {d.reports_to_us ? <Pill tone="good">Reports come to us</Pill> : <Pill tone="warn">Reports not set up</Pill>}
                  </div>
                  {d.policy && <p className="muted small">{POLICY[d.policy]?.[2]}</p>}
                  <PolicySteps policy={d.policy} />
                  {!d.reports_to_us && d.suggested && (
                    <div className="dmarc-record">
                      <p className="small">{d.record ? 'Replace the TXT record' : 'Add a TXT record'} at <code className="mono">_dmarc.{d.domain}</code> with:</p>
                      <pre className="mono small">{d.suggested}</pre>
                      <CopyButton text={d.suggested} label="Copy the record" />
                    </div>
                  )}
                  <p className="muted small">{d.reports ? `${d.reports} report${d.reports === 1 ? '' : 's'}, the latest covering up to ${fmtDateTime(d.last_report_at)}` : d.reports_to_us ? 'No reports yet: providers send one a day, so the first arrives within about 24 hours.' : ''}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </Section>

      <Section title="The last 30 days" description={data.days.length ? `${totals.passed.toLocaleString()} passed · ${totals.failed.toLocaleString()} failed` : null}>
        {data.days.length ? <><DayChart days={data.days} /><p className="muted small"><span className="dmarc-key-pass" /> Passed <span className="dmarc-key-fail" /> Failed: not proven to come from you</p></>
          : <EmptyState title="No reports yet">Once your DMARC record sends reports to us, they appear here within a day.</EmptyState>}
      </Section>

      {data.sources.length > 0 && (
        <Section title="Servers sending as you" description="Every server that sent email claiming to be your domain. Failing ones that aren’t yours are someone pretending to be you — or one of your services that needs setting up to sign your email." flush>
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Server</th><th>Emails</th><th>Result</th><th>Signed as</th>{data.can_manage && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {data.sources.map((s) => (
                  <tr key={s.ip}>
                    <td data-label="Server"><span className="mono">{s.ip}</span>{(s.ptr || s.network) && <div className="muted small">{s.ptr || s.network}</div>}</td>
                    <td data-label="Emails" className="nowrap">{s.messages.toLocaleString()}{s.failed ? <span className="muted small"> · {s.failed.toLocaleString()} failed</span> : ''}</td>
                    <td data-label="Result"><Pill tone={KIND[s.kind][1]}>{KIND[s.kind][0]}</Pill>{s.status === 'not_ours' && <span className="muted small"> · not yours</span>}</td>
                    <td data-label="Signed as" className="small mono">{s.signed_as.join(', ') || '—'}</td>
                    {data.can_manage && (
                      <td data-label="" className="nowrap">
                        {s.status !== 'ours' && <button type="button" className="btn btn-small btn-secondary" onClick={() => mark(s.ip, 'ours')}>It’s ours</button>}{' '}
                        {s.status !== 'not_ours' && s.failed > 0 && <button type="button" className="btn btn-small btn-secondary" onClick={() => mark(s.ip, 'not_ours')}>Not ours</button>}
                        {s.status !== 'unknown' && <button type="button" className="btn btn-small btn-link" onClick={() => mark(s.ip, 'unknown')}>Undo</button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {data.reporters.length > 0 && (
        <Section title="Who sends reports">
          <p className="small">{data.reporters.map((r) => `${r.name} (${r.reports})`).join(' · ')}</p>
        </Section>
      )}
    </div>
  );
}
